/**
 * Asset Register — type-driven modals, BIA relationships picker, and DataTable/chart
 * for the BIA-keyed bia_assets DynamoDB schema.
 *
 * Key behaviours:
 *  - applyAssetTypeVisibility: toggles cards/rows whose data-bia-types attribute does not
 *    include the currently selected type, and strips/restores the `required` attribute on
 *    hidden controls (so HTML5 validation never blocks on hidden inputs).
 *  - renderRelationshipPicker: builds one Select2 per allowed target type for the chosen
 *    source type; populates options from list_relationship_candidates AJAX. The picker's
 *    selections are serialised as JSON to submit_new_asset / edit_asset.
 *  - Single-asset page exposes window.populateEditAssetForm and window.refreshAssetTable
 *    for the per-asset and register-list flows respectively.
 */
(function($) {
    'use strict';

    var assetsTable = null;
    var assetChartActive = null;
    var ASSET_TIER_ORDER = [
        'Tier 1 – Critical',
        'Tier 2 – Essential',
        'Tier 3 – Important',
        'Tier 4 – Supporting',
        'Tier 5 – Non-Critical'
    ];
    var ASSET_TIER_COLORS = {
        'Tier 1 – Critical': 'rgba(220, 38, 38, 0.9)',
        'Tier 2 – Essential': 'rgba(234, 88, 12, 0.9)',
        'Tier 3 – Important': 'rgba(217, 119, 6, 0.9)',
        'Tier 4 – Supporting': '#5789bb',
        'Tier 5 – Non-Critical': '#314d76'
    };

    /**
     * Show/hide every [data-bia-types] block in the form whose comma-separated list does
     * not include the chosen $assetType value. Required attributes on hidden controls are
     * stashed in data-bia-required and removed; restored on show.
     *
     * Hidden controls are also marked `disabled` so that fields with the same `name`
     * attribute across different type sections (e.g. `vendor`, `hostingLocation`) do not
     * collide during form serialisation. Without this, FormData appends the visible value
     * followed by N empty values from the hidden sections, and PHP's $_POST keeps the
     * last (empty) one.
     */
    function applyAssetTypeVisibility($form, currentType) {
        if (!$form || !$form.length) return;
        $form.find('[data-bia-types]').each(function() {
            var $el = $(this);
            var raw = $el.attr('data-bia-types') || '';
            var allowed = raw.split(',').map(function(s) { return s.trim(); }).filter(Boolean);
            var visible = !!(currentType && allowed.indexOf(currentType) !== -1);
            $el.toggle(visible);
            $el.find('input, select, textarea').each(function() {
                var $f = $(this);
                if ($f.attr('data-bia-keep-enabled') === '1') {
                    return;
                }
                $f.prop('disabled', !visible);
                $f.prop('required', visible && $f.attr('data-bia-required') === '1');
            });
        });
    }

    /**
     * DOM id of the relationship picker container for the given form prefix.
     */
    function relationshipPickerElementId(prefix) {
        if (prefix === 'new') {
            return 'newAssetRelationshipPicker';
        }
        if (prefix === 'relOnly') {
            return 'editAssetRelationshipsOnlyPicker';
        }
        return 'editAssetRelationshipPicker';
    }

    function destroyRelationshipsOnlyPicker() {
        var $picker = $('#editAssetRelationshipsOnlyPicker');
        $picker.find('select.bia-rel-select').each(function() {
            var $s = $(this);
            if ($s.hasClass('select2-hidden-accessible')) {
                $s.select2('destroy');
            }
        });
        $picker.empty();
    }

    /**
     * Select2 `language` for a relationship dropdown, so the empty state names the asset
     * type instead of saying "No results found". Distinguishes "none of this type exist"
     * from "your search matched nothing".
     *
     * @param {jQuery} $select   The relationship <select>.
     * @param {string} typeName  Asset type, e.g. 'SaaS Application'.
     */
    function relationshipNoResultsLanguage($select, typeName) {
        return {
            noResults: function() {
                if (!$select.children('option').length) {
                    return 'No ' + typeName + ' Assets exist yet';
                }
                return 'No matching ' + typeName + ' Assets';
            }
        };
    }

    /**
     * Relationship picker: outbound (this → target) and/or inbound (peer → this), from allowlist.
     *
     * @param {Array}  [preselectedInbound] { fromType, fromID }[] when UI is reverse-only.
     */
    function renderRelationshipPicker(prefix, sourceType, excludeID, preselected, onAllDone, preselectedInbound) {
        var done = typeof onAllDone === 'function' ? onAllDone : null;
        var pickerId = relationshipPickerElementId(prefix);
        var $picker = $('#' + pickerId);
        if (!$picker.length) {
            if (done) {
                done();
            }
            return;
        }
        $picker.empty();

        var allowlist = (assetRegisterData && assetRegisterData.relationshipAllowlist) || {};
        var inboundMap = (assetRegisterData && assetRegisterData.inboundRelationshipSources) || {};
        var forwardTargets = allowlist[sourceType] || [];
        var reverseSources = inboundMap[sourceType] || [];
        var $modal = $picker.closest('.modal');
        var dropdownParent = $modal.length ? $modal : $(document.body);

        function finishOne(pendingRef) {
            if (--pendingRef.count === 0 && done) {
                done();
            }
        }

        if (!forwardTargets.length && !reverseSources.length) {
            $picker.append('<p class="text-muted small mb-0">No relationship targets are defined for this asset type.</p>');
            if (done) {
                done();
            }
            return;
        }

        var pending = { count: 0 };

        if (forwardTargets.length) {
            var preMap = {};
            (preselected || []).forEach(function(r) {
                if (!r || !r.toType) return;
                if (!preMap[r.toType]) preMap[r.toType] = [];
                preMap[r.toType].push(r.toID);
            });

            pending.count += forwardTargets.length;
            forwardTargets.forEach(function(targetType) {
                var idSafe = 'rel_' + prefix + '_' + targetType.replace(/[^A-Za-z0-9]/g, '_');
                var html =
                    '<div class="row mb-3" data-rel-target-type="' + escapeAttr(targetType) + '">' +
                        '<div class="col-md-12">' +
                            '<label for="' + idSafe + '" class="form-label">' + escapeHtml(targetType) + 's</label>' +
                            '<select class="form-select bia-rel-select" id="' + idSafe + '" name="rel_' + escapeAttr(targetType) + '" multiple data-link-direction="outbound" data-target-type="' + escapeAttr(targetType) + '" data-prefix="' + prefix + '">' +
                            '</select>' +
                        '</div>' +
                    '</div>';
                $picker.append(html);

                var $select = $('#' + idSafe);
                $.ajax({
                    url: assetRegisterData.ajax_url,
                    type: 'POST',
                    data: {
                        action: 'list_relationship_candidates',
                        nonce: assetRegisterData.nonce,
                        targetType: targetType,
                        excludeID: excludeID || ''
                    },
                    success: function(resp) {
                        if (!resp.success) return;
                        var pre = preMap[targetType] || [];
                        (resp.data.candidates || []).forEach(function(c) {
                            var $opt = $('<option></option>').val(c.assetID).text(c.assetName || c.assetID);
                            if (pre.indexOf(c.assetID) !== -1) {
                                $opt.prop('selected', true);
                            }
                            $select.append($opt);
                        });
                        if ($.fn.select2) {
                            $select.select2({
                                width: '100%',
                                dropdownParent: dropdownParent,
                                closeOnSelect: false,
                                placeholder: 'Select ' + targetType + 's',
                                language: relationshipNoResultsLanguage($select, targetType)
                            });
                        }
                    },
                    complete: function() {
                        finishOne(pending);
                    }
                });
            });
        }

        if (!forwardTargets.length && reverseSources.length) {
            var intro = (assetRegisterData && assetRegisterData.relationshipPickerReverseIntro) || '';
            if (intro) {
                $picker.append('<p class="text-muted small mb-3" role="note">' + escapeHtml(intro) + '</p>');
            }

            var preMapRev = {};
            (preselectedInbound || []).forEach(function(r) {
                if (!r || !r.fromType) return;
                if (!preMapRev[r.fromType]) preMapRev[r.fromType] = [];
                preMapRev[r.fromType].push(r.fromID);
            });

            pending.count += reverseSources.length;
            reverseSources.forEach(function(peerType) {
                var idSafe = 'rel_' + prefix + '_in_' + peerType.replace(/[^A-Za-z0-9]/g, '_');
                var html =
                    '<div class="row mb-3" data-rel-peer-type="' + escapeAttr(peerType) + '">' +
                        '<div class="col-md-12">' +
                            '<label for="' + idSafe + '" class="form-label">' + escapeHtml(peerType) + '</label>' +
                            '<small class="text-muted d-block mb-1">Links as: ' + escapeHtml(peerType) + ' → ' + escapeHtml(sourceType) + '</small>' +
                            '<select class="form-select bia-rel-select" id="' + idSafe + '" name="rel_in_' + escapeAttr(peerType) + '" multiple data-link-direction="inbound" data-peer-type="' + escapeAttr(peerType) + '" data-prefix="' + prefix + '">' +
                            '</select>' +
                        '</div>' +
                    '</div>';
                $picker.append(html);

                var $select = $('#' + idSafe);
                $.ajax({
                    url: assetRegisterData.ajax_url,
                    type: 'POST',
                    data: {
                        action: 'list_relationship_candidates',
                        nonce: assetRegisterData.nonce,
                        targetType: peerType,
                        excludeID: excludeID || ''
                    },
                    success: function(resp) {
                        if (!resp.success) return;
                        var pre = preMapRev[peerType] || [];
                        (resp.data.candidates || []).forEach(function(c) {
                            var $opt = $('<option></option>').val(c.assetID).text(c.assetName || c.assetID);
                            if (pre.indexOf(c.assetID) !== -1) {
                                $opt.prop('selected', true);
                            }
                            $select.append($opt);
                        });
                        if ($.fn.select2) {
                            $select.select2({
                                width: '100%',
                                dropdownParent: dropdownParent,
                                closeOnSelect: false,
                                placeholder: 'Select ' + peerType + ' assets',
                                language: relationshipNoResultsLanguage($select, peerType)
                            });
                        }
                    },
                    complete: function() {
                        finishOne(pending);
                    }
                });
            });
        }

        if (pending.count === 0 && done) {
            done();
        }
    }

    function escapeHtml(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
    }
    function escapeAttr(s) {
        return escapeHtml(s);
    }

    /**
     * Collect relationship picker rows: outbound { toType, toID, _dir } and inbound { fromType, fromID, _dir }.
     */
    function collectRelationships(prefix) {
        var pickerId = relationshipPickerElementId(prefix);
        var $picker = $('#' + pickerId);
        var out = [];
        $picker.find('select.bia-rel-select').each(function() {
            var $s = $(this);
            if (!$s.is(':visible')) {
                return;
            }
            var dir = $s.attr('data-link-direction') || 'outbound';
            var vals = $s.val() || [];
            if (dir === 'inbound') {
                var peerType = $s.attr('data-peer-type');
                vals.forEach(function(id) {
                    out.push({ _dir: 'inbound', fromType: peerType, fromID: id });
                });
            } else {
                var targetType = $s.attr('data-target-type');
                vals.forEach(function(id) {
                    out.push({ _dir: 'outbound', toType: targetType, toID: id });
                });
            }
        });
        return out;
    }

    function init() {
        initDataTable();
        initAssetViewToggle();
        initAssetModalSelect2();
        initNewAssetForm();
        initEditAssetForm();
        initDeleteAsset();
        initRelationshipsOnlyModal();
        initFilters();
        initChartFilters();
        initAssetTypeBindings();
    }

    function initRelationshipsOnlyModal() {
        if (!$('#assetRelationshipsModal').length) {
            return;
        }
        $(document).on('click', '#assetRelationshipsModalSave', function() {
            var $m = $('#assetRelationshipsModal');
            var assetId = $m.attr('data-asset-id') || '';
            var assetType = $m.attr('data-asset-type') || '';
            if (!assetId || typeof assetRegisterData === 'undefined') {
                return;
            }
            var $btn = $(this);
            var orig = $btn.text();
            $btn.prop('disabled', true).text('Saving...');
            syncRelationshipsFromForm('relOnly', assetId, assetType, function() {
                $btn.prop('disabled', false).text(orig);
                var el = document.getElementById('assetRelationshipsModal');
                var modal = el && typeof bootstrap !== 'undefined' ? bootstrap.Modal.getInstance(el) : null;
                if (modal) {
                    modal.hide();
                }
                showToast('Relationships updated', 'success');
                window.location.reload();
            });
        });
    }

    function initAssetTypeBindings() {
        var $newForm = $('#newAssetForm');
        var $editForm = $('#editAssetForm');

        applyAssetTypeVisibility($newForm, '');
        applyAssetTypeVisibility($editForm, '');

        $(document).on('change', '#assetType', function() {
            var t = $(this).val() || '';
            applyAssetTypeVisibility($newForm, t);
            renderRelationshipPicker('new', t, '', []);
        });
        $(document).on('change', '#editAssetType', function() {
            var t = $(this).val() || '';
            applyAssetTypeVisibility($editForm, t);
        });
    }

    /**
     * Tear down Select2 instances inside an asset form (modal-safe).
     */
    function destroyAssetFormSelect2($form) {
        if (!$form || !$form.length || !$.fn.select2) return;
        $form.find('select').each(function() {
            var $s = $(this);
            $s.off('select2:open.assetS2');
            if ($s.hasClass('select2-hidden-accessible')) {
                $s.select2('destroy');
            }
        });
    }

    /** Same pattern as new-risk modal: overlay until Select2 is attached (avoids layout shift). */
    function showAssetModalLoader(loaderId) {
        var $l = $('#' + loaderId);
        if ($l.length) {
            $l.removeClass('d-none');
        }
    }
    function hideAssetModalLoader(loaderId) {
        var $l = $('#' + loaderId);
        if ($l.length) {
            $l.addClass('d-none');
        }
    }

    /**
     * Initialize Select2 on every static select in the form. Relationship-picker selects
     * are initialised on demand by renderRelationshipPicker; skip them here.
     */
    function initAssetFormSelect2($form) {
        if (!$form || !$form.length || !$.fn.select2) {
            return;
        }
        var $modal = $form.closest('.modal');
        var dropdownParent = $modal.length ? $modal : $(document.body);

        $form.find('select').each(function() {
            var $s = $(this);
            if ($s.hasClass('bia-rel-select')) return;
            if ($s.hasClass('select2-hidden-accessible')) {
                $s.select2('destroy');
            }

            var isMulti = $s.prop('multiple');
            var opts = {
                width: '100%',
                dropdownParent: dropdownParent
            };

            if (isMulti) {
                opts.closeOnSelect = false;
                opts.placeholder = 'Select options';
            } else {
                opts.allowClear = !$s.prop('required');
                var emptyOpt = $s.find('option[value=""]').first();
                opts.placeholder = emptyOpt.length ? $.trim(emptyOpt.text()) || 'Select' : 'Select';
            }

            $s.select2(opts);

            $s.off('select2:open.assetS2').on('select2:open.assetS2', function() {
                setTimeout(function() {
                    var el = document.querySelector('.select2-container--open .select2-search__field');
                    if (el) el.focus();
                }, 0);
            });
        });

        $form.find('select').each(function() {
            var $sel = $(this);
            if ($sel.hasClass('select2-hidden-accessible')) {
                $sel.trigger('change');
            }
        });
    }

    function initAssetModalSelect2() {
        var $newModal = $('#newAssetModal');
        var $editModal = $('#editAssetModal');
        if (!$newModal.length && !$editModal.length) return;

        $newModal.on('show.bs.modal', function() {
            showAssetModalLoader('new-asset-modal-loader');
        });
        $newModal.on('shown.bs.modal', function() {
            initAssetFormSelect2($('#newAssetForm'));
            applyAssetTypeVisibility($('#newAssetForm'), $('#assetType').val() || '');
            window.setTimeout(function() {
                hideAssetModalLoader('new-asset-modal-loader');
            }, 0);
        });
        $newModal.on('hidden.bs.modal', function() {
            destroyAssetFormSelect2($('#newAssetForm'));
            var form = document.getElementById('newAssetForm');
            if (form) form.reset();
            $('#newAssetRelationshipPicker').empty();
            showAssetModalLoader('new-asset-modal-loader');
        });

        $editModal.on('show.bs.modal', function() {
            showAssetModalLoader('edit-asset-modal-loader');
        });
        $editModal.on('hidden.bs.modal', function() {
            destroyAssetFormSelect2($('#editAssetForm'));
            $('#editAssetRelationshipPicker').empty();
            showAssetModalLoader('edit-asset-modal-loader');
        });

        var $relOnlyModal = $('#assetRelationshipsModal');
        if ($relOnlyModal.length) {
            $relOnlyModal.on('hidden.bs.modal', function() {
                destroyRelationshipsOnlyPicker();
                $('#asset-relationships-modal-content').addClass('d-none');
                showAssetModalLoader('asset-relationships-modal-loader');
                $relOnlyModal.removeAttr('data-asset-id').removeAttr('data-asset-type');
            });
        }
    }

    function toggleAssetRegisterHeader() {
        var $container = $('#asset-table-container');
        var hasRows = $container.find('#userAssetsTable tbody tr').length > 0;
        $container.find('.asset-register-header').toggleClass('d-none', !hasRows);
    }

    function hideAssetTableLoader() {
        var $loader = $('#asset-table-loader');
        var $view = $('#asset-table-view');
        if ($loader.length) $loader.addClass('d-none');
        if ($view.length) $view.removeClass('risk-table-view-loading');
    }

    function initDataTable() {
        var table = document.querySelector('#userAssetsTable');
        if (!table) {
            hideAssetTableLoader();
            toggleAssetRegisterHeader();
            return;
        }

        if (table.closest('.datatable-wrapper')) {
            hideAssetTableLoader();
            return;
        }

        var DataTableClass = null;
        if (typeof DataTable !== 'undefined') DataTableClass = DataTable;
        else if (typeof simpleDatatables !== 'undefined' && simpleDatatables.DataTable) DataTableClass = simpleDatatables.DataTable;
        else if (typeof window.DataTable !== 'undefined') DataTableClass = window.DataTable;

        if (!DataTableClass) {
            console.error('[Asset Register] DataTable class not found');
            hideAssetTableLoader();
            return;
        }

        try {
            assetsTable = new DataTableClass(table, {
                sortable: true,
                paging: true,
                perPage: 25,
                perPageSelect: false,
                searchable: true,
                columns: [
                    { select: 0, sort: 'asc' },
                    { select: 1, headerClass: 'text-center', cellClass: 'text-center' },
                    { select: 2, headerClass: 'text-center', cellClass: 'text-center' },
                    { select: 3, headerClass: 'text-center', cellClass: 'text-center' },
                    { select: 4, headerClass: 'text-center', cellClass: 'text-center' },
                    { select: 5, sortable: false, searchable: false, headerClass: 'risk-actions-header text-center', cellClass: 'text-center' }
                ],
                labels: { placeholder: 'Search:', noRows: 'No assets found', info: 'Showing {start} to {end} of {rows} entries' }
            });

            setTimeout(function() {
                var tableWrapper = $(table).closest('.datatable-wrapper');
                if (!tableWrapper.length) tableWrapper = $('.datatable-wrapper').has('#userAssetsTable');
                var topContainer = tableWrapper.find('.datatable-top');
                var headerLeft = $('#asset-table-view .asset-header-left');
                var headerRight = $('#asset-table-view .asset-header-right');

                if (headerLeft.length && assetsTable && !headerLeft.find('.asset-table-search-input').length) {
                    var searchInput = $('<input type="search" class="form-control asset-table-search-input" placeholder="Search:" aria-controls="userAssetsTable">');
                    searchInput.prependTo(headerLeft);
                    searchInput.on('input keyup', function() {
                        var term = $(this).val();
                        if (typeof assetsTable.search === 'function') assetsTable.search(term);
                    });
                }

                var newBtn = $('.new-project-container .new-project-btn');
                if (newBtn.length && headerRight.length) {
                    newBtn.detach();
                    var buttonContainer = $('<div class="new-project-btn-container"></div>');
                    buttonContainer.append(newBtn);
                    headerRight.append(buttonContainer);
                    $('.new-project-container').hide();
                }

                if (topContainer.length) topContainer.hide();

                var chartViewEl = document.getElementById('asset-chart-view');
                if (chartViewEl && chartViewEl.style.display !== 'none') {
                    var c = $('.new-project-btn-container');
                    if (c.length) c.appendTo($('#asset-chart-view .asset-header-right'));
                }

                hideAssetTableLoader();
                toggleAssetRegisterHeader();
            }, 100);
        } catch (e) {
            console.error('[Asset Register] DataTable init error', e);
            hideAssetTableLoader();
        }
    }

    function initFilters() {
        var statusCheckboxes = $('.asset-status-checkbox');
        var statusAll = $('#asset-status-filter-all');
        var tierCheckboxes = $('.asset-tier-checkbox');
        var tierAll = $('#asset-tier-filter-all');
        var typeCheckboxes = $('.asset-type-checkbox');
        var typeAll = $('#asset-type-filter-all');

        function selectedValues($boxes) {
            var out = [];
            $boxes.filter(':checked').each(function() { out.push($(this).val()); });
            return out;
        }

        function apply() {
            var selectedStatuses = selectedValues(statusCheckboxes);
            var selectedTiers = selectedValues(tierCheckboxes);
            var selectedTypes = selectedValues(typeCheckboxes);
            var allStatuses = selectedStatuses.length === statusCheckboxes.length;
            var allTiers = selectedTiers.length === tierCheckboxes.length;
            var allTypes = selectedTypes.length === typeCheckboxes.length;
            var rows = document.querySelectorAll('#userAssetsTable tbody tr');
            rows.forEach(function(row) {
                var cells = row.querySelectorAll('td');
                if (cells.length < 4) return;
                var rowType = (cells[1].textContent || '').trim();
                var rowTier = (cells[2].textContent || '').trim();
                var rowStatus = (cells[3].textContent || '').trim();
                var statusMatch = allStatuses || selectedStatuses.indexOf(rowStatus) !== -1;
                var tierMatch = allTiers || selectedTiers.indexOf(rowTier) !== -1;
                var typeMatch = allTypes || selectedTypes.indexOf(rowType) !== -1;
                row.style.display = (statusMatch && tierMatch && typeMatch) ? '' : 'none';
            });
        }

        function bindGroup($all, $boxes) {
            $all.on('change', function(e) {
                e.stopPropagation();
                $boxes.prop('checked', $(this).is(':checked'));
                apply();
            });
            $boxes.on('change', function(e) {
                e.stopPropagation();
                $all.prop('checked', $boxes.filter(':checked').length === $boxes.length);
                apply();
            });
        }
        bindGroup(statusAll, statusCheckboxes);
        bindGroup(tierAll, tierCheckboxes);
        bindGroup(typeAll, typeCheckboxes);
        $('.asset-filter-container .dropdown-menu').on('click', function(e) { e.stopPropagation(); });
    }

    function initAssetViewToggle() {
        var tableView = document.getElementById('asset-table-view');
        var chartView = document.getElementById('asset-chart-view');
        if (!tableView || !chartView) return;

        function updateBtns(showTable) {
            document.querySelectorAll('#asset-table-view-btn').forEach(function(b) { b.classList.toggle('active', showTable); });
            document.querySelectorAll('#asset-chart-view-btn').forEach(function(b) { b.classList.toggle('active', !showTable); });
        }
        function showTable() {
            tableView.style.display = 'block';
            chartView.style.display = 'none';
            updateBtns(true);
            moveBtn('table');
        }
        function showChart() {
            tableView.style.display = 'none';
            chartView.style.display = 'block';
            updateBtns(false);
            moveBtn('chart');
            ensureAssetChart();
        }
        function moveBtn(view) {
            var container = $('.new-project-btn-container');
            if (!container.length) return;
            var target = view === 'chart' ? $('#asset-chart-view .asset-header-right') : $('#asset-table-view .asset-header-right');
            if (target.length) container.appendTo(target);
        }

        var saved = (typeof localStorage !== 'undefined' && localStorage.getItem('assetRegisterViewPreference')) || 'table';
        if (saved === 'chart') showChart(); else showTable();

        $(document).on('click', '#asset-table-view-btn', function() {
            showTable();
            if (typeof localStorage !== 'undefined') localStorage.setItem('assetRegisterViewPreference', 'table');
        });
        $(document).on('click', '#asset-chart-view-btn', function() {
            showChart();
            if (typeof localStorage !== 'undefined') localStorage.setItem('assetRegisterViewPreference', 'chart');
        });
    }

    function getCheckedValues(selector) {
        var out = [];
        $(selector).filter(':checked').each(function() { out.push($(this).val()); });
        return out;
    }

    function getChartFilters() {
        var $typeBoxes = $('.asset-chart-type-checkbox');
        var $tierBoxes = $('.asset-chart-tier-checkbox');
        var typeSel = getCheckedValues('.asset-chart-type-checkbox');
        var tierSel = getCheckedValues('.asset-chart-tier-checkbox');
        return {
            types: typeSel,
            tiers: tierSel,
            allTypes: $typeBoxes.length === 0 || typeSel.length === $typeBoxes.length,
            allTiers: $tierBoxes.length === 0 || tierSel.length === $tierBoxes.length
        };
    }

    function buildChartDataForAssetTypes(data) {
        if (!data || typeof data !== 'object') {
            return { labels: [], datasets: [] };
        }
        var filters = getChartFilters();
        var labels = Object.keys(data).filter(function(at) {
            return filters.allTypes || filters.types.indexOf(at) !== -1;
        });
        var tierOrder = ASSET_TIER_ORDER.filter(function(t) {
            return filters.allTiers || filters.tiers.indexOf(t) !== -1;
        });
        var datasets = tierOrder.map(function(tier) {
            var values = labels.map(function(assetType) {
                var row = data[assetType];
                return (row && row[tier] !== undefined) ? row[tier] : 0;
            });
            return {
                label: tier,
                data: values,
                backgroundColor: ASSET_TIER_COLORS[tier] || 'rgba(128,128,128,0.9)',
                stack: 'stack0'
            };
        });
        return { labels: labels, datasets: datasets };
    }

    function initChartFilters() {
        var $container = $('.asset-chart-filter-container');
        if (!$container.length) return;

        var $typeBoxes = $('.asset-chart-type-checkbox');
        var $typeAll = $('#asset-chart-type-filter-all');
        var $tierBoxes = $('.asset-chart-tier-checkbox');
        var $tierAll = $('#asset-chart-tier-filter-all');

        function refresh() {
            if (assetChartActive) {
                ensureAssetChart();
            }
        }

        function bindGroup($all, $boxes) {
            $all.on('change', function(e) {
                e.stopPropagation();
                $boxes.prop('checked', $(this).is(':checked'));
                refresh();
            });
            $boxes.on('change', function(e) {
                e.stopPropagation();
                $all.prop('checked', $boxes.filter(':checked').length === $boxes.length);
                refresh();
            });
        }
        bindGroup($typeAll, $typeBoxes);
        bindGroup($tierAll, $tierBoxes);
        $container.find('.dropdown-menu').on('click', function(e) { e.stopPropagation(); });
    }

    function getAssetChartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2,
            onClick: function(evt, elements, chart) {
                if (elements.length === 0) return;
                var el = elements[0];
                var assetType = chart.data.labels[el.index];
                var tier = chart.data.datasets[el.datasetIndex].label;
                var count = chart.data.datasets[el.datasetIndex].data[el.index];
                if (count > 0) openAssetChartSegmentModal(assetType, tier);
            },
            scales: {
                x: {
                    stacked: true,
                    title: { display: true, text: 'Asset Type' },
                    ticks: { maxRotation: 45, minRotation: 45 }
                },
                y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Number of assets' } }
            },
            plugins: {
                legend: { display: true, position: 'right' },
                tooltip: {
                    callbacks: {
                        label: function(ctx) {
                            var n = ctx.parsed.y;
                            return (ctx.dataset.label || '') + ': ' + n + ' asset' + (n !== 1 ? 's' : '');
                        }
                    }
                }
            }
        };
    }

    function hideAssetChartLoader() {
        var $cv = $('#asset-chart-view');
        if ($cv.length) {
            $cv.removeClass('asset-chart-view-loading');
        }
        $('#asset-chart-page-loader').addClass('d-none');
    }

    function ensureAssetChart() {
        if (typeof Chart === 'undefined') {
            hideAssetChartLoader();
            return;
        }
        var data = window.assetChartData;
        if (!data || typeof data !== 'object') {
            hideAssetChartLoader();
            return;
        }

        var chartPayload = buildChartDataForAssetTypes(data);
        var options = getAssetChartOptions();

        function createOrUpdate(canvasId, chartData, existing) {
            var canvas = document.getElementById(canvasId);
            if (!canvas) return null;
            if (existing) {
                existing.data.labels = chartData.labels;
                existing.data.datasets = chartData.datasets;
                existing.update();
                return existing;
            }
            return new Chart(canvas.getContext('2d'), {
                type: 'bar',
                data: { labels: chartData.labels, datasets: chartData.datasets },
                options: options
            });
        }

        assetChartActive = createOrUpdate('assetRegisterChartActive', chartPayload, assetChartActive);
        hideAssetChartLoader();
    }

    function openAssetChartSegmentModal(assetType, tier) {
        var $modal = $('#assetChartSegmentModal');
        var $title = $modal.find('#assetChartSegmentModalLabel');
        var $loading = $('#assetChartSegmentModalLoading');
        var $list = $('#assetChartSegmentModalList');
        var $empty = $('#assetChartSegmentModalEmpty');

        if (!$modal.length) return;

        $title.text('Assets: ' + assetType + ' – ' + tier);
        $list.empty().hide();
        $empty.hide().text('No assets in this category.');
        $loading.show();

        var modalEl = document.getElementById('assetChartSegmentModal');
        var modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
        modal.show();

        $.ajax({
            url: assetRegisterData.ajax_url,
            type: 'POST',
            data: {
                action: 'get_assets_by_asset_type_tier',
                nonce: assetRegisterData.nonce,
                assetType: assetType,
                tier: tier
            },
            success: function(response) {
                $loading.hide();
                if (response.success && response.data.assets && response.data.assets.length > 0) {
                    response.data.assets.forEach(function(a) {
                        var $a = $('<a class="list-group-item list-group-item-action">')
                            .attr('href', a.url || '#')
                            .text(a.asset_title || a.asset_id);
                        $list.append($a);
                    });
                    $list.show();
                } else {
                    $empty.show();
                }
            },
            error: function() {
                $loading.hide();
                $empty.text('Unable to load assets.').show();
            }
        });
    }

    function updateAssetChartData(chartData) {
        if (chartData) window.assetChartData = chartData;
        var chartView = document.getElementById('asset-chart-view');
        if (chartView && chartView.style.display !== 'none' && assetChartActive) {
            ensureAssetChart();
        }
    }

    function appendJsonMultis(formData, prefix) {
        var p = prefix === 'edit' ? 'edit' : '';
        var bf  = $('#' + (p ? p + 'BusinessFunctionsSupported' : 'businessFunctionsSupported')).val() || [];
        var pld = $('#' + (p ? p + 'PrimaryLossDrivers' : 'primaryLossDrivers')).val() || [];
        var rdt = $('#' + (p ? p + 'RegulatedDataTypes' : 'regulatedDataTypes')).val() || [];
        formData.set('businessFunctionsSupported', JSON.stringify(bf));
        formData.set('primaryLossDrivers', JSON.stringify(pld));
        formData.set('regulatedDataTypes', JSON.stringify(rdt));
    }

    function initNewAssetForm() {
        var $form = $('#newAssetForm');
        if (!$form.length) return;

        $form.on('submit', function(e) {
            e.preventDefault();
            var $submitBtn = $form.find('button[type="submit"]');
            var orig = $submitBtn.text();
            var assetType = $('#assetType').val() || '';

            if (!assetType) {
                showToast('Select an asset type', 'error');
                return;
            }
            if (assetType === 'Data Asset') {
                var rdt = $('#regulatedDataTypes').val() || [];
                if (!rdt.length) {
                    showToast('Select at least one regulated data type', 'error');
                    return;
                }
            }
            $submitBtn.prop('disabled', true).text('Creating...');

            var formData = new FormData(this);
            formData.append('action', 'submit_new_asset');
            formData.append('nonce', assetRegisterData.nonce);
            appendJsonMultis(formData, '');
            formData.set('relationships', JSON.stringify(collectRelationships('new')));

            $.ajax({
                url: assetRegisterData.ajax_url,
                type: 'POST',
                data: formData,
                processData: false,
                contentType: false,
                success: function(response) {
                    $submitBtn.prop('disabled', false).text(orig);
                    if (response.success) {
                        var modal = bootstrap.Modal.getInstance(document.getElementById('newAssetModal'));
                        if (modal) modal.hide();
                        var aid = response.data && response.data.asset_id;
                        var base = (assetRegisterData.home_url || '').replace(/\/+$/, '');
                        if (aid && base) {
                            window.location.href = base + '/asset/' + aid;
                        } else {
                            showToast('Asset created successfully', 'success');
                            refreshAssetTable();
                        }
                    } else {
                        showToast('Error: ' + (response.data && response.data.message ? response.data.message : 'Failed'), 'error');
                    }
                },
                error: function() {
                    $submitBtn.prop('disabled', false).text(orig);
                    showToast('An error occurred while creating the asset', 'error');
                }
            });
        });
    }

    function initEditAssetForm() {
        var $form = $('#editAssetForm');
        if (!$form.length) return;

        $form.on('submit', function(e) {
            e.preventDefault();
            var $submitBtn = $form.find('button[type="submit"]');
            var orig = $submitBtn.text();
            var assetType = $('#editAssetType').val() || '';

            if (!assetType) {
                showToast('Asset type is missing', 'error');
                return;
            }
            if (assetType === 'Data Asset') {
                var rdt = $('#editRegulatedDataTypes').val() || [];
                if (!rdt.length) {
                    showToast('Select at least one regulated data type', 'error');
                    return;
                }
            }
            $submitBtn.prop('disabled', true).text('Saving...');

            var formData = new FormData();
            $form.find('input, select, textarea').each(function() {
                var $el = $(this);
                if ($el.is(':disabled')) {
                    return;
                }
                var name = $el.attr('name');
                if (!name) return;
                if ($el.is('select') && $el.prop('multiple')) {
                    var vals = $el.val() || [];
                    vals.forEach(function(v) { formData.append(name, v); });
                    return;
                }
                if ($el.is(':checkbox') || $el.is(':radio')) {
                    if ($el.is(':checked')) {
                        formData.append(name, $el.val());
                    }
                    return;
                }
                formData.append(name, $el.val());
            });
            // assetType select is disabled to prevent edits; copy its value through anyway.
            formData.set('assetType', assetType);
            formData.append('action', 'edit_asset');
            formData.append('nonce', assetRegisterData.nonce);
            appendJsonMultis(formData, 'edit');

            $.ajax({
                url: assetRegisterData.ajax_url,
                type: 'POST',
                data: formData,
                processData: false,
                contentType: false,
                success: function(response) {
                    if (response.success) {
                        syncRelationshipsFromForm('edit', $('#editAssetIdField').val(), assetType, function() {
                            $submitBtn.prop('disabled', false).text(orig);
                            var modal = bootstrap.Modal.getInstance(document.getElementById('editAssetModal'));
                            if (modal) modal.hide();
                            showToast('Asset updated successfully', 'success');
                            refreshAssetTable();
                        });
                    } else {
                        $submitBtn.prop('disabled', false).text(orig);
                        showToast('Error: ' + (response.data && response.data.message ? response.data.message : 'Failed'), 'error');
                    }
                },
                error: function() {
                    $submitBtn.prop('disabled', false).text(orig);
                    showToast('An error occurred while updating the asset', 'error');
                }
            });
        });
    }

    /**
     * Diff the picker selection against the asset's existing relationships and issue
     * add_asset_relationship / remove_asset_relationship for the delta. Saves a round
     * trip when nothing changed.
     */
    function syncRelationshipsFromForm(prefix, assetId, assetType, done) {
        var allowlist = (assetRegisterData && assetRegisterData.relationshipAllowlist) || {};
        var inboundMap = (assetRegisterData && assetRegisterData.inboundRelationshipSources) || {};
        var forwardAllowed = allowlist[assetType] || [];
        var reverseSources = inboundMap[assetType] || [];
        var reverseMode = forwardAllowed.length === 0 && reverseSources.length > 0;

        $.ajax({
            url: assetRegisterData.ajax_url,
            type: 'POST',
            data: {
                action: 'list_asset_relationships',
                nonce: assetRegisterData.nonce,
                assetID: assetId
            },
            success: function(resp) {
                if (!resp || !resp.success || !resp.data) {
                    if (typeof done === 'function') done();
                    return;
                }

                var picked = collectRelationships(prefix);
                var ops = [];

                if (reverseMode) {
                    var existingInbound = resp.data.inbound || [];
                    var existing = [];
                    existingInbound.forEach(function(r) {
                        if (reverseSources.indexOf(r.fromType) !== -1) {
                            existing.push({ fromType: r.fromType, fromID: r.fromID });
                        }
                    });
                    var existingMap = {};
                    existing.forEach(function(r) { existingMap[r.fromType + '#' + r.fromID] = true; });

                    var desired = picked.filter(function(r) { return r._dir === 'inbound'; });
                    var desiredMap = {};
                    desired.forEach(function(r) { desiredMap[r.fromType + '#' + r.fromID] = true; });

                    desired.forEach(function(r) {
                        if (!existingMap[r.fromType + '#' + r.fromID]) {
                            ops.push({
                                action: 'add_asset_relationship',
                                payload: { fromID: r.fromID, toType: assetType, toID: assetId }
                            });
                        }
                    });
                    existing.forEach(function(r) {
                        if (!desiredMap[r.fromType + '#' + r.fromID]) {
                            ops.push({
                                action: 'remove_asset_relationship',
                                payload: { fromID: r.fromID, toType: assetType, toID: assetId }
                            });
                        }
                    });
                } else {
                    var existing = resp.data.outbound || [];
                    var existingMap = {};
                    existing.forEach(function(r) { existingMap[r.toType + '#' + r.toID] = true; });

                    var desired = picked.filter(function(r) { return r._dir !== 'inbound'; });
                    var desiredMap = {};
                    desired.forEach(function(r) { desiredMap[r.toType + '#' + r.toID] = true; });

                    desired.forEach(function(r) {
                        if (!existingMap[r.toType + '#' + r.toID]) {
                            ops.push({
                                action: 'add_asset_relationship',
                                payload: { fromID: assetId, toType: r.toType, toID: r.toID }
                            });
                        }
                    });
                    existing.forEach(function(r) {
                        if (!desiredMap[r.toType + '#' + r.toID]) {
                            ops.push({
                                action: 'remove_asset_relationship',
                                payload: { fromID: assetId, toType: r.toType, toID: r.toID }
                            });
                        }
                    });
                }

                if (!ops.length) {
                    if (typeof done === 'function') done();
                    return;
                }
                (function runRelOp(i) {
                    if (i >= ops.length) {
                        if (typeof done === 'function') done();
                        return;
                    }
                    var op = ops[i];
                    var data = $.extend({
                        action: op.action,
                        nonce: assetRegisterData.nonce
                    }, op.payload);
                    $.ajax({
                        url: assetRegisterData.ajax_url,
                        type: 'POST',
                        data: data,
                        complete: function() {
                            runRelOp(i + 1);
                        }
                    });
                })(0);
            },
            error: function() {
                if (typeof done === 'function') done();
            }
        });
    }

    /**
     * If stored owner/advisor is not in the corporate list (legacy data), append option so Select2 can show it.
     */
    function ensureAssetCorporateSelect($select, value) {
        var values;
        if (Array.isArray(value)) {
            values = value.slice();
        } else if (value === null || value === undefined || value === '') {
            values = [];
        } else {
            values = String(value).split(',').map(function(v) { return v.trim(); }).filter(Boolean);
        }
        values.forEach(function(v) {
            var v = String(v);
            var found = false;
            $select.find('option').each(function() {
                if ($(this).attr('value') === v) { found = true; return false; }
            });
            if (!found) {
                $select.append($('<option/>').attr('value', v).text(v));
            }
        });
        if ($select.prop('multiple')) {
            $select.val(values);
        } else {
            $select.val(values[0] || '');
        }
    }

    /**
     * Populate the edit modal from a Dynamo row, including identity fields and the
     * relationships picker. Asset type is read-only on edit.
     */
    window.populateEditAssetForm = function(assetId) {
        $.ajax({
            url: assetRegisterData.ajax_url,
            type: 'POST',
            data: {
                action: 'get_asset_data_for_edit',
                asset_id: assetId,
                nonce: assetRegisterData.nonce
            },
            success: function(response) {
                if (!response.success) {
                    showToast('Error: ' + (response.data && response.data.message ? response.data.message : 'Failed to load'), 'error');
                    return;
                }
                var d = response.data;

                showAssetModalLoader('edit-asset-modal-loader');
                destroyAssetFormSelect2($('#editAssetForm'));

                $('#editAssetIdField').val(d.assetID || '');
                $('#editAssetName').val(d.assetName || '');
                $('#editAssetDescription').val(d.assetDescription || '');
                $('#editAssetType').val(d.assetType || '');
                ensureAssetCorporateSelect($('#editAssetOwner'), d.assetOwner || '');
                ensureAssetCorporateSelect($('#editAssetCustodian'), d.assetCustodian || '');
                $('#editAvailabilityRequirement').val(d.availabilityRequirement || '');
                $('#editTargetRtoTier').val(d.targetRtoTier || '');
                $('#editTargetRpoTier').val(d.targetRpoTier || '');
                $('#editImpactLossConfidentiality').val(d.impactLossConfidentiality || '');
                $('#editImpactLossIntegrity').val(d.impactLossIntegrity || '');
                $('#editImpactLossAvailability').val(d.impactLossAvailability || '');
                $('#editImpactHumanSafety').val(d.impactHumanSafety || '');
                $('#editDataVolume').val(d.dataVolume || '');
                $('#editDataClassification').val(d.dataClassification || '');
                $('#editAssetStatus').val(d.assetStatus || 'Active');

                $('#editBusinessFunctionsSupported').val(d.businessFunctionsSupported || []);
                $('#editPrimaryLossDrivers').val(d.primaryLossDrivers || []);
                $('#editRegulatedDataTypes').val(d.regulatedDataTypes || []);

                // Identity fields per type. Keys like `vendor` repeat across types but each type
                // has its own row [data-bia-types] — `#edit_${key}` would only ever set the first
                // duplicate id in the DOM (wrong row for most asset types).
                var identityMap = (assetRegisterData && assetRegisterData.identityFieldsByType) || {};
                var $editForm = $('#editAssetForm');
                Object.keys(identityMap).forEach(function(type) {
                    (identityMap[type] || []).forEach(function(f) {
                        var $row = $editForm.find('.row[data-bia-types]').filter(function() {
                            return $(this).attr('data-bia-types') === type;
                        });
                        // Multi-select identity fields (e.g. dataType) submit as `key[]`.
                        var $field = $row.find('[name="' + f.key + '"], [name="' + f.key + '[]"]');
                        if ($field.length) {
                            var v = d[f.key];
                            if ($field.prop('multiple')) {
                                var vals = v == null ? [] : (Array.isArray(v) ? v : [v]);
                                $field.val(vals);
                            } else {
                                $field.val(v == null ? '' : v);
                            }
                        }
                    });
                });

                var strip = function(x) { return x ? String(x).split(' ')[0] : ''; };
                $('#editLastReviewDate').val(strip(d.lastReviewDate));
                $('#editNextReviewDate').val(strip(d.nextReviewDate));
                $('#editAssetReviewNotes').val(d.assetReviewNotes || '');

                applyAssetTypeVisibility($('#editAssetForm'), d.assetType || '');

                var editEl = document.getElementById('editAssetModal');
                var modal = bootstrap.Modal.getInstance(editEl) || new bootstrap.Modal(editEl);
                $('#editAssetModal').one('shown.bs.modal', function onEditAssetModalShown() {
                    initAssetFormSelect2($('#editAssetForm'));
                    renderRelationshipPicker('edit', d.assetType || '', d.assetID || '', d.relationships || [], null, d.relationshipsInbound || []);
                    window.setTimeout(function() {
                        hideAssetModalLoader('edit-asset-modal-loader');
                    }, 0);
                });
                modal.show();
            },
            error: function() {
                showToast('Error loading asset data', 'error');
            }
        });
    };

    /**
     * Single-asset page: open modal with the Relationships picker (outbound and/or inbound peers).
     */
    window.openAssetRelationshipsModal = function(assetId) {
        if (!assetId || typeof assetRegisterData === 'undefined') {
            return;
        }
        var modalEl = document.getElementById('assetRelationshipsModal');
        if (!modalEl) {
            return;
        }
        showAssetModalLoader('asset-relationships-modal-loader');
        destroyRelationshipsOnlyPicker();
        $('#asset-relationships-modal-content').addClass('d-none');

        $.ajax({
            url: assetRegisterData.ajax_url,
            type: 'POST',
            data: {
                action: 'get_asset_data_for_edit',
                asset_id: assetId,
                nonce: assetRegisterData.nonce
            },
            success: function(response) {
                if (!response.success) {
                    hideAssetModalLoader('asset-relationships-modal-loader');
                    showToast('Error: ' + (response.data && response.data.message ? response.data.message : 'Failed to load'), 'error');
                    return;
                }
                var d = response.data;
                var $modal = $('#assetRelationshipsModal');
                $modal.attr('data-asset-id', d.assetID || '');
                $modal.attr('data-asset-type', d.assetType || '');

                var modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
                $modal.one('shown.bs.modal', function() {
                    renderRelationshipPicker('relOnly', d.assetType || '', d.assetID || '', d.relationships || [], function() {
                        hideAssetModalLoader('asset-relationships-modal-loader');
                        window.requestAnimationFrame(function() {
                            $('#asset-relationships-modal-content').removeClass('d-none');
                        });
                    }, d.relationshipsInbound || []);
                });
                modal.show();
            },
            error: function() {
                hideAssetModalLoader('asset-relationships-modal-loader');
                showToast('Error loading asset data', 'error');
            }
        });
    };

    function initDeleteAsset() {
        $(document).on('click', '#confirmDeleteAssetBtn', function() {
            var assetId = $('#deleteAssetId').val();
            if (!assetId) return;
            if (typeof assetRegisterData === 'undefined') {
                showToast('Asset register script is not loaded correctly', 'error');
                return;
            }
            $('#deleteAssetConfirmContent').hide();
            $('#deleteAssetLoadingContent').show();
            $.ajax({
                url: assetRegisterData.ajax_url,
                type: 'POST',
                data: { action: 'delete_asset', assetId: assetId, nonce: assetRegisterData.nonce },
                success: function(response) {
                    $('#deleteAssetLoadingContent').hide();
                    $('#deleteAssetConfirmContent').show();
                    var delEl = document.getElementById('deleteAssetModal');
                    var modal = delEl && typeof bootstrap !== 'undefined' ? bootstrap.Modal.getInstance(delEl) : null;
                    if (modal) modal.hide();
                    if (response.success) {
                        showToast('Asset deleted successfully', 'success');
                        var p = window.location.pathname || '';
                        if (/\/asset\/[a-f0-9]{32}\/?$/i.test(p)) {
                            var base = (assetRegisterData.home_url || '/').replace(/\/+$/, '');
                            window.location.href = base + '/asset-register';
                        } else {
                            refreshAssetTable();
                        }
                    } else {
                        showToast('Error: ' + (response.data && response.data.message ? response.data.message : 'Failed'), 'error');
                    }
                },
                error: function() {
                    $('#deleteAssetLoadingContent').hide();
                    $('#deleteAssetConfirmContent').show();
                    showToast('Error deleting asset', 'error');
                }
            });
        });
    }

    window.showDeleteAssetModal = function(assetId, title) {
        $('#deleteAssetId').val(assetId);
        $('#deleteAssetTitle').text(title || '');
        $('#deleteAssetLoadingContent').hide();
        $('#deleteAssetConfirmContent').show();
        var el = document.getElementById('deleteAssetModal');
        if (!el || typeof bootstrap === 'undefined') {
            return;
        }
        bootstrap.Modal.getOrCreateInstance(el).show();
    };

    window.refreshAssetTable = function() {
        var p = window.location.pathname || '';
        if (/\/asset\/[a-f0-9]{32}\/?$/i.test(p)) {
            window.location.reload();
            return;
        }
        var $loader = $('#asset-table-loader');
        var $container = $('#asset-table-container');
        $loader.removeClass('d-none').show();
        $.ajax({
            url: assetRegisterData.ajax_url,
            type: 'POST',
            data: { action: 'refresh_asset_table', nonce: assetRegisterData.nonce },
            success: function(response) {
                $loader.hide();
                if (response.success) {
                    if (assetsTable) {
                        assetsTable.destroy();
                        assetsTable = null;
                    }
                    $container.html(response.data.html);
                    initDataTable();
                    toggleAssetRegisterHeader();
                    if (response.data.chartData) updateAssetChartData(response.data.chartData);
                }
            },
            error: function() { $loader.hide(); }
        });
    };

    window.showToast = function(message, type) {
        $('.toast-container').remove();
        var bgClass = type === 'success' ? 'bg-success' : type === 'error' ? 'bg-danger' : type === 'warning' ? 'bg-warning' : 'bg-info';
        var toastHtml = '<div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">' +
            '<div class="toast align-items-center text-white ' + bgClass + '" role="alert">' +
            '<div class="d-flex"><div class="toast-body">' + message + '</div>' +
            '<button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button></div></div></div>';
        $('body').append(toastHtml);
        var $toastElement = $('.toast');
        var toast = new bootstrap.Toast($toastElement[0], { delay: 3000 });
        toast.show();
        $toastElement.on('hidden.bs.toast', function() { $(this).closest('.toast-container').remove(); });
    };

    $(document).ready(init);
})(jQuery);
